import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell, BellOff, Bookmark, Check, Inbox, LogOut, Megaphone, Phone, Plus, Search, ShieldAlert, ShoppingBag, Trophy, Users, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { isPlatformStaff } from '@/lib/moderationApi'
import { achieveApi } from '@/lib/achieve'
import { useAppStore } from '@/store/app'
import { useHotkeyStore } from '@/store/hotkeys'
import { isDesktop } from '@/lib/desktop'
import { useIsMobile } from '@/lib/mobile'
import { channelName, useSocialStore } from '@/store/social'
import { cx } from '@/lib/cx'
import { openOfficialServer } from '@/lib/official'
import { ChannelType, type Channel } from '@/types/api'
import { Avatar } from '../Avatar'
import { GroupAvatar } from '../GroupAvatar'
import { avatarUrl } from '@/lib/avatar'
import { userContextHandler } from '@/store/contextMenu'
import { useGuildStore } from '@/store/guilds'
import { isUnread } from '@/lib/guildUi'
import { DM_SETTINGS, ownMuted } from '@/lib/muting'
import { ContextMenu, MenuItem, MenuSeparator } from '../ContextMenu'
import { usePresenceStore } from '@/store/presence'
import { customStatusOf, isOnline } from '@/lib/presence'
import { Modal } from '../settings/Modal'
import { FriendPicker } from './FriendPicker'
import { InboxButton } from './InboxButton'
import sidebar from '../ChannelSidebar.module.css'
import styles from './HomeSidebar.module.css'
import { StyledName } from '../StyledName'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

export function HomeSidebar() {
  const staff = useAuthStore(s => isPlatformStaff(s.user?.flags ?? 0))
  const openModeration = useAppStore(s => s.openModeration)
  const mobile = useIsMobile()
  const hasTitlebar = isDesktop && !mobile
  const view = useAppStore((s) => s.view)
  const setView = useAppStore((s) => s.setView)
  const showContent = useAppStore((s) => s.showContent)
  const channels = useSocialStore((s) => s.channels)
  const readStates = useSocialStore((s) => s.readStates)
  const presences = useSocialStore((s) => s.presences)
  const activities = usePresenceStore((s) => s.activities)
  const calls = useSocialStore((s) => s.calls)
  const closeDM = useSocialStore((s) => s.closeDM)
  const openDM = useSocialStore((s) => s.openDM)
  const createGroup = useSocialStore((s) => s.createGroup)
  const pendingIn = useSocialStore((s) => Object.values(s.relationships).filter((r) => r.type === 3).length)
  const [creating, setCreating] = useState(false)
  const [leaving, setLeaving] = useState<Channel | null>(null)
  const setSwitcherOpen = useHotkeyStore((s) => s.setSwitcherOpen)
  const [opening, setOpening] = useState(false)
  const dmSettings = useGuildStore((s) => s.settings[DM_SETTINGS])
  // меню группы по правой кнопке; у беседы один на один — меню собеседника с пунктами беседы
  const [groupMenu, setGroupMenu] = useState<{ channel: Channel; x: number; y: number } | null>(null)
  const closeGroupMenu = useCallback(() => setGroupMenu(null), [])

  const list = Object.values(channels)
    .filter((c) => c.type === ChannelType.DM || c.type === ChannelType.GroupDM)
    .sort((a, b) => (b.last_message_id ?? '0').length - (a.last_message_id ?? '0').length || ((b.last_message_id ?? '0') > (a.last_message_id ?? '0') ? 1 : -1))
  const page = view.kind === 'home' && 'page' in view ? view.page : null
  // новые достижения — счётчиком у «Достижений», пока раздел не открыли
  const achievements = useQuery({ queryKey: ['achievements'], queryFn: achieveApi.list, staleTime: 60_000 })
  const newAchievements = achievements.data?.filter((a) => a.new).length ?? 0

  const close = async (ch: Channel) => {
    const active = view.kind === 'home' && 'channelId' in view && view.channelId === ch.id
    await closeDM(ch.id)
    if (active) setView({ kind: 'home', page: 'friends' })
  }

  return (
    <aside className={sidebar.sidebar} aria-label="Личные сообщения">
      <div className={styles.search}>
        <button
          type="button"
          className={styles.searchButton}
          aria-haspopup="dialog"
          onClick={() => setSwitcherOpen(true)}
        >
          <Search size={16} aria-hidden="true" />
          <span>Найти или начать беседу</span>
        </button>
      </div>
      <div className={cx(sidebar.channels, 'scroller')}>
        <button type="button" className={cx(styles.nav, page === 'friends' && styles.navActive)} onClick={() => { setView({ kind: 'home', page: 'friends' }); showContent() }} aria-current={page === 'friends' ? 'page' : undefined}>
          <Users size={22} />
          <span>Друзья</span>
          {pendingIn ? <span className={sidebar.mentions}>{pendingIn}</span> : null}
        </button>
        {!hasTitlebar ? <InboxButton className={styles.nav} activeClassName={styles.navActive}>
          <Inbox size={22} /><span>Входящие</span>
        </InboxButton> : null}
        <button type="button" className={cx(styles.nav, page === 'saved' && styles.navActive)} onClick={() => { setView({ kind: 'home', page: 'saved' }); showContent() }} aria-current={page === 'saved' ? 'page' : undefined}>
          <Bookmark size={22} /><span>Сохранённое</span>
        </button>
        {/* магазин и достижения на виду, а не только в настройках */}
        <button type="button" className={cx(styles.nav, page === 'shop' && styles.navActive)} onClick={() => { setView({ kind: 'home', page: 'shop' }); showContent() }} aria-current={page === 'shop' ? 'page' : undefined}>
          <ShoppingBag size={22} />
          <span>Магазин</span>
        </button>
        <button type="button" className={cx(styles.nav, page === 'achievements' && styles.navActive)} onClick={() => { setView({ kind: 'home', page: 'achievements' }); showContent() }} aria-current={page === 'achievements' ? 'page' : undefined}>
          <Trophy size={22} />
          <span>Достижения</span>
          {newAchievements && page !== 'achievements' ? <span className={sidebar.mentions}>{newAchievements}</span> : null}
        </button>
        {/* официальный сервер всегда на виду, как «Друзья»: новости, вопросы, поддержка голосом */}
        <button
          type="button"
          className={cx(styles.nav, styles.navOfficial)}
          disabled={opening}
          onClick={() => {
            setOpening(true)
            void openOfficialServer().finally(() => setOpening(false))
          }}
        >
          <Megaphone size={22} />
          <span>Наш сервер</span>
        </button>
        {staff ? <button type="button" className={styles.nav} onClick={openModeration}><ShieldAlert size={22} /><span>Модерация</span></button> : null}
        <div className={styles.sectionHeader}>
          <span>Личные сообщения</span>
          <button type="button" data-tooltip="Создать беседу" aria-label="Создать беседу" onClick={() => setCreating(true)}><Plus size={16} /></button>
        </div>
        {list.length === 0 ? (
          <p className={styles.empty}>Начните беседу через поиск или вкладку «Друзья».</p>
        ) : null}
        {list.map((ch) => {
          const group = ch.type === ChannelType.GroupDM
          const r = group ? undefined : ch.recipients?.[0]
          const name = channelName(ch)
          const active = view.kind === 'home' && 'channelId' in view && view.channelId === ch.id
          // заглушённая беседа серая и без красного счётчика: новое видно только по яркому имени
          const muted = ownMuted(dmSettings, ch.id)
          const mentions = muted ? 0 : (readStates[ch.id]?.mention_count ?? 0)
          const unread = mentions > 0 || (muted && isUnread(ch, readStates[ch.id]))
          const members = (ch.recipients?.length ?? 0) + 1
          const status = r ? (presences[r.id] ?? 'offline') : 'offline'
          const custom = r && isOnline(status) ? customStatusOf(activities[r.id]) : ''

          return (
            <div
              key={ch.id}
              className={cx(styles.dm, active && styles.dmActive, muted && styles.dmMuted)}
              onContextMenu={
                r
                  ? userContextHandler(r.id, { user: r, dmChannelId: ch.id })
                  : (e) => {
                      e.preventDefault()
                      setGroupMenu({ channel: ch, x: e.clientX, y: e.clientY })
                    }
              }
            >
              <button type="button" className={styles.dmButton} onClick={() => setView({ kind: 'home', channelId: ch.id })} aria-current={active ? 'page' : undefined}>
                {group ? (
                  <GroupAvatar recipients={ch.recipients} />
                ) : (
                  <Avatar initial={name.slice(0, 1).toUpperCase()} color={r ? colorOf(r.id) : '#5865F2'} size={32} status={isOnline(status) ? status : 'offline'} className={styles.dmAvatar} src={r ? avatarUrl(r.id, r.avatar) : undefined} decoration={r?.avatar_decoration_data} />
                )}
                <span className={styles.dmText}>
                  <span className={cx(styles.dmName, unread && styles.dmUnread)}><StyledName name={name} style={group ? null : r?.display_name_styles} /></span>
                  {group ? (
                    <span className={styles.dmSub}>{members} {plural(members, 'участник', 'участника', 'участников')}</span>
                  ) : custom ? (
                    <span className={styles.dmSub} data-tooltip={custom}>{custom}</span>
                  ) : null}
                </span>
                {calls[ch.id] ? <Phone size={14} className={styles.dmCall} aria-label="Идёт звонок" /> : null}
                {muted ? <BellOff size={14} className={styles.dmMutedIcon} aria-label="Беседа заглушена" /> : null}
                {mentions > 0 ? <span className={sidebar.mentions}>{mentions}</span> : null}
              </button>
              <button
                type="button"
                className={styles.dmClose}
                data-tooltip={group ? 'Покинуть группу' : 'Закрыть'}
                aria-label={group ? `Покинуть группу ${name}` : `Закрыть беседу с ${name}`}
                onClick={() => (group ? setLeaving(ch) : void close(ch))}
              >
                <X size={16} />
              </button>
            </div>
          )
        })}
      </div>
      {creating ? (
        <FriendPicker
          title="Выберите друзей"
          submitLabel={(n) => (n <= 1 ? 'Открыть личные сообщения' : `Создать группу (${n + 1})`)}
          max={9}
          onSubmit={async (ids) => {
            const id = ids.length === 1 && ids[0] ? await openDM(ids[0]) : await createGroup(ids)
            setView({ kind: 'home', channelId: id })
          }}
          onClose={() => setCreating(false)}
        />
      ) : null}
      {groupMenu ? <GroupMenu channel={groupMenu.channel} x={groupMenu.x} y={groupMenu.y} onLeave={() => setLeaving(groupMenu.channel)} onClose={closeGroupMenu} /> : null}
      {leaving ? (
        <Modal title="Покинуть группу" onClose={() => setLeaving(null)}>
          <p className={styles.confirmText}>Покинуть группу «{channelName(leaving)}»? Вернуться можно будет только по приглашению участника.</p>
          <div className={styles.confirmButtons}>
            <button type="button" className={styles.cancel} onClick={() => setLeaving(null)}>Отмена</button>
            <button type="button" className={styles.confirm} onClick={() => { const ch = leaving; setLeaving(null); void close(ch) }}>Покинуть группу</button>
          </div>
        </Modal>
      ) : null}
    </aside>
  )
}

/** Меню группы: прочитать, заглушить, покинуть. */
function GroupMenu({ channel, x, y, onLeave, onClose }: { channel: Channel; x: number; y: number; onLeave: () => void; onClose: () => void }) {
  const readState = useSocialStore((s) => s.readStates[channel.id])
  const ack = useSocialStore((s) => s.ack)
  const muted = useGuildStore((s) => ownMuted(s.settings[DM_SETTINGS], channel.id))
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const unread = isUnread(channel, readState) || (readState?.mention_count ?? 0) > 0
  return (
    <ContextMenu x={x} y={y} label={`Группа ${channelName(channel)}`} onClose={onClose}>
      <MenuItem icon={<Check size={16} />} label="Отметить как прочитанное" disabled={!unread} onClick={() => { void ack(channel.id); onClose() }} />
      <MenuItem
        icon={muted ? <Bell size={16} /> : <BellOff size={16} />}
        label={muted ? 'Включить уведомления' : 'Заглушить группу'}
        onClick={() => {
          void updateSettings(DM_SETTINGS, { channel_overrides: { [channel.id]: { muted: !muted } } }).catch(() => undefined)
          onClose()
        }}
      />
      <MenuSeparator />
      <MenuItem icon={<LogOut size={16} />} label="Покинуть группу" danger onClick={() => { onClose(); onLeave() }} />
    </ContextMenu>
  )
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}
